import React from 'react';
import cx from 'classnames';
import Container from '@/container';
import {Grid, Row, Col} from '@/grid';
import {SectionSpacedTopLgToDouble} from '@/section';
import {withFlexHolder} from '@/hocs/withFlex';
import {H2, H3, P} from '@/typography';
import {withNeutral800, withNeutral900} from '@/hocs/withColors';
import {withMarginBottomMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import IntroBox from '@/ui/introbox';
import {ListTerms} from '@/ui/lists/list-icons';
import styles from './split-feature-section.module.scss';
import type {SplitFeatureSectionProps} from './types';

const FlexHolderRow = withFlexHolder(Row);
const H2Neutral900 = withNeutral900(H2);
const PNeutral800 = withNeutral800(P);
const H3MarginedMd = withMarginBottomMd(withNeutral900(H3));

/** Grid forwards `children` via createProps; PropTypes-only export omits it from types. */
const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

type FlexColProps = React.ComponentProps<typeof Col> & {
  first?: string;
  last?: string;
  lgOffset?: number;
};

const FlexCol = Col as React.ComponentType<FlexColProps>;

const SplitFeatureSection = ({
  eyebrow,
  title,
  description,
  highlights,
  highlightsTitle = 'Feature highlights:',
  imageSide = 'left',
  placeholderVariant = 'lavender',
  layout = 'offset',
  imgPath,
  responsiveImages,
  imageAlt = '',
  className = '',
  id
}: SplitFeatureSectionProps) => {
  const imageOnRight = imageSide === 'right';
  const equalLayout = layout === 'equal';

  const mediaContent =
    imgPath && responsiveImages?.desktopImageUrl && responsiveImages?.mobileImageUrl ? (
      <PictureWrapper
        imgPath={imgPath}
        responsiveImages={responsiveImages}
        imageTypeFallback="jpg"
        altImage={imageAlt}
        loading="lazy"
      />
    ) : (
      <div
        className={cx(styles.placeholder, styles[`placeholder--${placeholderVariant}`])}
        aria-hidden="true"
      />
    );

  const textContent = (
    <IntroBox>
      <p className="heading-eyebrow">{eyebrow}</p>
      <H2Neutral900>{title}</H2Neutral900>
      <PNeutral800>{description}</PNeutral800>
      <H3MarginedMd className={cx(styles['highlights-title'], 'heading-4')}>
        {highlightsTitle}
      </H3MarginedMd>
      <ListTerms>
        {highlights.map(highlight => (
          <li key={highlight}>{highlight}</li>
        ))}
      </ListTerms>
    </IntroBox>
  );

  // equal: col-lg-6 | col-lg-6 (40px gap)
  // offset: col-lg-6 | col-lg-5 col-lg-offset-1 (image left)
  //         col-lg-5 | col-lg-6 col-lg-offset-1 (image right)
  const primaryCol = imageOnRight ? (
    <FlexCol xs={12} lg={equalLayout ? 6 : 5}>
      {textContent}
    </FlexCol>
  ) : (
    <FlexCol xs={12} lg={6}>
      <div className={styles.media}>{mediaContent}</div>
    </FlexCol>
  );

  const secondaryCol = imageOnRight ? (
    <FlexCol xs={12} lg={6} {...(equalLayout ? {} : {lgOffset: 1})} first="xs" last="lg">
      <div className={styles.media}>{mediaContent}</div>
    </FlexCol>
  ) : (
    <FlexCol xs={12} lg={equalLayout ? 6 : 5} {...(equalLayout ? {} : {lgOffset: 1})}>
      {textContent}
    </FlexCol>
  );

  return (
    <SectionSpacedTopLgToDouble id={id} className={className}>
      <Container>
        <FluidGrid fluid>
          <FlexHolderRow
            middle="xs"
            start="lg"
            className={equalLayout ? styles['row-equal'] : undefined}>
            {primaryCol}
            {secondaryCol}
          </FlexHolderRow>
        </FluidGrid>
      </Container>
    </SectionSpacedTopLgToDouble>
  );
};

export default SplitFeatureSection;
export type {
  SplitFeatureSectionImageSide,
  SplitFeatureSectionLayout,
  SplitFeatureSectionPlaceholderVariant,
  SplitFeatureSectionProps,
  SplitFeatureSectionResponsiveImages
} from './types';
